import React, { useEffect, useMemo, useState } from 'react';

import { toast } from 'react-toastify';

import { useRoleSwitcher } from '../../../../hooks/useRoleSwitcher';
import AppModal from '../../../../ui-components/AppModal';
import Dropdown from '../../../../ui-components/Dropdown';
import type { AppModalActionProps } from '../../../../model/appModalActionProps';

import {
  useEditSystemNotificationMutation,
  useLazyGetSystemNotificationDetailQuery,
} from '../systemNotificationsApi';

import type { SystemNotificationDetail } from '../model';

const SUPER_ADMIN_ROLE_CODES = ['HBADMIN', 'SUPER_ADMIN', 'SUPERADMIN'];

interface SystemNotificationEditModalProps {
  isOpen: boolean;
  notificationTemplateId?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

const SystemNotificationEditModal: React.FC<
  SystemNotificationEditModalProps
> = ({ isOpen, notificationTemplateId, onClose, onSuccess }) => {
  const [systemNotificationContent, setSystemNotificationContent] =
    useState('');
  const [status, setStatus] = useState('');
  const { activeRoleCode } = useRoleSwitcher();
  const canManageStatus = SUPER_ADMIN_ROLE_CODES.includes(
    (activeRoleCode || '').trim().toUpperCase()
  );

  const [fetchDetail, { data, isFetching, error }] =
    useLazyGetSystemNotificationDetailQuery();
  const [updateNotification, { isLoading }] =
    useEditSystemNotificationMutation();

  useEffect(() => {
    if (isOpen && notificationTemplateId) {
      fetchDetail(notificationTemplateId);
    }
  }, [fetchDetail, notificationTemplateId, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSystemNotificationContent('');
      setStatus('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const detail = data?.data;
    if (detail) {
      setSystemNotificationContent(detail.systemNotificationContent ?? '');
      setStatus(detail.status ?? 'active');
    }
  }, [data, isOpen]);

  const templateInfo = useMemo<SystemNotificationDetail | undefined>(
    () => data?.data,
    [data]
  );

  const handleSave = async () => {
    if (!notificationTemplateId) return;
    try {
      await updateNotification({
        id: notificationTemplateId,
        systemNotificationContent,
        ...(canManageStatus && status ? { status } : {}),
      }).unwrap();
      onSuccess?.();
      onClose();
    } catch (saveError: any) {
      toast.error(
        saveError?.data?.message ||
          saveError?.message ||
          'Failed to update system notification'
      );
    }
  };

  const actions: AppModalActionProps[] = [
    {
      label: 'Cancel',
      variant: 'secondary',
      onClick: onClose,
      disabled: isLoading,
    },
    {
      label: 'Save changes',
      variant: 'primary',
      onClick: handleSave,
      loading: isLoading,
      disabled: isLoading,
    },
  ];

  return (
    <AppModal
      isOpen={isOpen}
      title="Edit System Notification"
      onClose={onClose}
      actions={actions}
      hideFooter={false}
      variant="form"
      closeOnOverlayClick
      maxWidth="max-w-3xl"
    >
      {isFetching ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-neutral-500">Loading template...</p>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-error-500">
            Unable to load system notification template. Please try again.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          <div>
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Template Title
            </label>
            <input
              type="text"
              value={templateInfo?.templateTitle ?? ''}
              readOnly
              className="input mt-1.5 bg-neutral-50 dark:bg-neutral-900 cursor-not-allowed border-neutral-200 dark:border-neutral-800"
            />
            <p className="mt-1.5 text-xs text-neutral-500">
              Template title is predefined and cannot be changed
            </p>
          </div>

          {canManageStatus ? (
            <div>
              <Dropdown
                label="Status"
                value={status}
                setValue={(value) => setStatus(String(value))}
                options={STATUS_OPTIONS}
                labelKey="label"
                valueKey="value"
                isCreatable={false}
                placeholder="Select status"
              />
              <p className="mt-1.5 text-xs text-neutral-500">
                Only SuperAdmin can activate or inactivate this template.
              </p>
            </div>
          ) : null}

          <div>
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              System Notification Message{' '}
              <span className="text-error-500">*</span>
            </label>
            <textarea
              value={systemNotificationContent}
              onChange={(event) =>
                setSystemNotificationContent(event.target.value)
              }
              className="input mt-1.5 min-h-36 resize-y"
              placeholder="Enter system notification message"
              maxLength={500}
            />
            <p className="mt-1.5 text-xs text-neutral-500">
              {systemNotificationContent.length}/500 characters
            </p>
          </div>
        </div>
      )}
    </AppModal>
  );
};

export default SystemNotificationEditModal;
